// Liste des paiements (H4-05, H4-06) : filtres (état, canal, anomalie), référence serveur, canal
// et prestataire, montant, quittance, état ; export CSV journalisé ; paiement au guichet.
import { Bandeau, Bouton, EtatVide, Filtres, Lien, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { CreditCard, Download } from 'lucide-react';
import { useState } from 'react';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { CODES_CANAL, LIBELLES_CANAL, LIBELLES_ETAT_PAIEMENT, francs, libelle, tonPaiement, type PaiementReponse } from './donnees';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { PaiementGuichet } from './PaiementGuichet';
import { telechargerFichier, usePaiements } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

export function puces(p: Pick<PaiementReponse, 'etat' | 'en_anomalie'>) {
  const t = tonPaiement(p);
  return <PuceEtat ton={t.ton} libelle={t.libelle} />;
}

export function PagePaiements() {
  const droits = useDroitsRecettes();
  const [filtres, setFiltres] = useFiltresPersistes('recettes-paiements', { etat: '', canal: '', en_anomalie: '' });
  const f = { etat: filtres.etat ?? '', canal: filtres.canal ?? '', en_anomalie: filtres.en_anomalie ?? '' };
  const liste = usePaiements(f);
  const [erreurExport, setErreurExport] = useState<string | null>(null);

  const colonnes: ColonneTableau<PaiementReponse>[] = [
    { id: 'reference', entete: 'Référence', enTeteLigne: true, masquable: false, valeurTri: (p) => p.reference_serveur, cellule: (p) => <Lien href={`/recettes/paiements/${p.id}`} className={commun.reference}>{p.reference_serveur}</Lien> },
    { id: 'canal', entete: 'Canal', valeurTri: (p) => p.canal, cellule: (p) => (
      <>
        <div>{libelle(LIBELLES_CANAL, p.canal)}</div>
        <div className={styles.sous}>{p.prestataire}{p.reference_prestataire ? `, ${p.reference_prestataire}` : ''}</div>
      </>
    ) },
    { id: 'montant', entete: 'Montant', numerique: true, valeurTri: (p) => p.montant, cellule: (p) => <span className={styles.montant}>{francs(p.montant)}</span> },
    { id: 'creances', entete: 'Créances visées', cellule: (p) => `${p.creances_visees.length}` },
    { id: 'initie', entete: 'Initié le', valeurTri: (p) => p.initie_le, cellule: (p) => dateHeure(p.initie_le) },
    { id: 'confirme', entete: 'Confirmé le', valeurTri: (p) => p.confirme_le ?? null, cellule: (p) => (p.confirme_le ? dateHeure(p.confirme_le) : p.refuse_le ? `refusé le ${dateHeure(p.refuse_le)}` : 'non confirmé') },
    { id: 'quittance', entete: 'Quittance', cellule: (p) => (p.quittance ? <span className={styles.mono}>{p.quittance.numero}</span> : 'aucune') },
    { id: 'etat', entete: 'État', valeurTri: (p) => p.etat, cellule: puces },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Paiements"
        description="Paiements en ligne, de caisse ou de banque. La confirmation d'un prestataire est vérifiée par le serveur (signature, référence, montant, devise, bénéficiaire) ; chaque notification est journalisée avec son traitement."
        actions={
          <>
            {droits.exporter ? (
              <Bouton variante="secondaire" icone={Download} onClick={() => { setErreurExport(null); telechargerFichier('/recettes/paiements/export', 'paiements.csv', f).catch((e: unknown) => setErreurExport(messageErreur(e))); }}>
                Exporter en CSV
              </Bouton>
            ) : null}
            {droits.creer ? <PaiementGuichet /> : null}
          </>
        }
      />
      <NavigationFinances />
      {erreurExport ? <Bandeau type="erreur" onFermer={() => setErreurExport(null)}>Export impossible : {erreurExport}</Bandeau> : null}
      <Filtres
        libelle="Filtres des paiements"
        groupes={[
          { id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Tous' }, ...Object.entries(LIBELLES_ETAT_PAIEMENT).map(([valeur, l]) => ({ valeur, libelle: l }))] },
          { id: 'canal', libelle: 'Canal', options: [{ valeur: '', libelle: 'Tous' }, ...CODES_CANAL.map((c) => ({ valeur: c, libelle: LIBELLES_CANAL[c] ?? c }))] },
          { id: 'en_anomalie', libelle: 'Anomalie', options: [{ valeur: '', libelle: 'Tous' }, { valeur: 'oui', libelle: 'En anomalie' }, { valeur: 'non', libelle: 'Sans anomalie' }] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-A" fonction="La liste des paiements">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun paiement" icone={CreditCard}>
            <p>Aucun paiement ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Paiements" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(p) => p.id} cleColonnes="recettes-paiements" />
        )}
      </EtatRequete>
    </>
  );
}
